import type {
  BoardBuilderProjection,
  BoardBuilderProjections
} from '@library/types/boardBuilder';
import { DateTime } from 'luxon';
import { determineScoreError } from '@library/utils';
import { dropWhile } from 'ramda';
import { findElementIndexInObjectList } from '@bo-utils/common';
import { isTimeAfter } from '@library/utils/dateTime';

export const INVALID_PROJECTION_VALUE = 'INVALID_PROJECTION_VALUE';
export const INVALID_START_TIME = 'INVALID_START_TIME';
export const INVALID_BOARD_TIME = 'INVALID_BOARD_TIME';
export const INVALID_DISPLAY_TIME = 'INVALID_DISPLAY_TIME';
export const getValidationErrors = ({
  key,
  projection
}: {
  key: string;
  projection: BoardBuilderProjection;
}): BoardBuilderProjection['errors'] => {
  const { averageScore, boardTime, startTime, projectionValue, errors } =
    projection;

  if (['projectionValue', 'averageScore'].includes(key)) {
    const hasInvalidProjectionValueError = errors?.includes(
      INVALID_PROJECTION_VALUE
    );
    if (!projectionValue && !hasInvalidProjectionValueError) {
      return errors?.length
        ? [...errors, INVALID_PROJECTION_VALUE]
        : [INVALID_PROJECTION_VALUE];
    }
    if (!averageScore && projectionValue && hasInvalidProjectionValueError) {
      return dropWhile(
        (error) => error === INVALID_PROJECTION_VALUE,
        errors ?? []
      );
    }

    if (averageScore) {
      const isInvalid = determineScoreError({
        averageScore,
        projectionValue
      });

      if (isInvalid && !hasInvalidProjectionValueError) {
        return errors?.length
          ? [...errors, INVALID_PROJECTION_VALUE]
          : [INVALID_PROJECTION_VALUE];
      }
      if (!isInvalid && hasInvalidProjectionValueError) {
        return dropWhile(
          (error) => error === INVALID_PROJECTION_VALUE,
          errors ?? []
        );
      }
    }
  }
  if (key === 'startTime') {
    const isValidStartTime = isTimeAfter({
      dateValue: DateTime.fromISO(startTime)
    });
    const hasInvalidStartTimeError = errors?.includes(INVALID_START_TIME);

    if (isValidStartTime && hasInvalidStartTimeError) {
      return dropWhile((error) => error === INVALID_START_TIME, errors ?? []);
    }
    if (!isValidStartTime && !hasInvalidStartTimeError) {
      return errors?.length
        ? [...errors, INVALID_START_TIME]
        : [INVALID_START_TIME];
    }
  }
  if (['boardTime', 'startTime'].includes(key)) {
    const isValidBoardTime =
      !!boardTime &&
      !!startTime &&
      isTimeAfter({
        dateValue: DateTime.fromISO(startTime),
        dateToCompareAgainst: DateTime.fromISO(boardTime)
      });

    const hasInvalidBoardTimeError = errors?.includes(INVALID_BOARD_TIME);
    if (isValidBoardTime && hasInvalidBoardTimeError) {
      return dropWhile((error) => error === INVALID_BOARD_TIME, errors ?? []);
    }
    if (!isValidBoardTime && !hasInvalidBoardTimeError) {
      return errors?.length
        ? [...errors, INVALID_BOARD_TIME]
        : [INVALID_BOARD_TIME];
    }
  }

  return projection.errors;
};

export const findBoardBuilderProjectionIndex = ({
  projectionId,
  projections
}: {
  projectionId: string;
  projections: BoardBuilderProjections;
}): number =>
  findElementIndexInObjectList(projections, 'projectionId', projectionId);
